import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Home, Sparkles, Key, Building2, Eye, Wind, CheckCircle } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Home,
      title: 'Regular House Cleaning',
      description: 'Keep your home consistently clean with our regular cleaning service. Perfect for busy families and professionals.',
      included: [
        'Dusting all surfaces and furniture',
        'Vacuuming and mopping floors',
        'Kitchen cleaning (benchtops, sink, appliances)',
        'Bathroom sanitization',
        'Rubbish removal',
        'Making beds and tidying',
      ],
      image: 'https://images.unsplash.com/photo-1653242370332-e332a8103763?auto=format&fit=crop&q=80',
    },
    {
      icon: Sparkles,
      title: 'Deep Cleaning',
      description: 'Thorough, intensive cleaning that reaches every corner. Ideal for spring cleaning or preparing for special events.',
      included: [
        'All regular cleaning tasks',
        'Inside ovens, fridges, and cabinets',
        'Window tracks and sills',
        'Skirting boards and light fixtures',
        'Behind appliances',
        'Detailed bathroom grout cleaning',
      ],
      image: 'https://images.pexels.com/photos/5591907/pexels-photo-5591907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Key,
      title: 'End of Lease Cleaning',
      description: 'Get your bond back with our comprehensive end-of-lease cleaning. We meet all property management requirements.',
      included: [
        'Complete deep clean of entire property',
        'Carpet steam cleaning',
        'Wall spot cleaning',
        'Oven and rangehood cleaning',
        'Window cleaning inside and out',
        'Bond-back guarantee',
      ],
      image: 'https://images.pexels.com/photos/10568358/pexels-photo-10568358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Building2,
      title: 'Office & Commercial Cleaning',
      description: 'Professional cleaning for offices, retail spaces, and commercial properties. Flexible scheduling to minimize disruption.',
      included: [
        'Desk and workspace cleaning',
        'Floor vacuuming and mopping',
        'Restroom sanitization',
        'Kitchen and break room cleaning',
        'Rubbish removal and recycling',
        'High-touch surface disinfection',
      ],
      image: 'https://images.pexels.com/photos/3316924/pexels-photo-3316924.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Eye,
      title: 'Window Cleaning',
      description: 'Crystal-clear windows that let the sunshine in. Interior and exterior window cleaning for homes and businesses.',
      included: [
        'Interior window cleaning',
        'Exterior window cleaning',
        'Window tracks and frames',
        'Screen cleaning',
        'Streak-free finish',
        'Safety equipment for high windows',
      ],
      image: 'https://images.unsplash.com/photo-1597406462638-5700a53b53d8?auto=format&fit=crop&q=80',
    },
    {
      icon: Wind,
      title: 'Carpet Cleaning',
      description: 'Professional carpet steam cleaning that removes dirt, stains, and allergens. Safe for pets and children.',
      included: [
        'Pre-treatment of stains',
        'Hot water extraction (steam cleaning)',
        'Eco-friendly cleaning solutions',
        'Fast drying time',
        'Deodorizing',
        'Pet stain and odor removal',
      ],
      image: 'https://images.unsplash.com/photo-1740694125619-8e72f30eaeee?auto=format&fit=crop&q=80',
    },
  ];

  const faqs = [
    {
      question: 'Do I need to provide cleaning supplies?',
      answer: 'No, we bring all our own professional-grade, eco-friendly cleaning supplies and equipment. However, if you have specific products you\'d like us to use, we\'re happy to accommodate.',
    },
    {
      question: 'Are your cleaners insured and police-checked?',
      answer: 'Yes, absolutely. All our cleaning staff are fully insured, police-checked, and professionally trained to ensure your peace of mind and safety.',
    },
    {
      question: 'How long does a typical cleaning take?',
      answer: 'It depends on the size of your property and the service type. A typical 3-bedroom house takes 3-4 hours for regular cleaning and 5-6 hours for deep cleaning.',
    },
    {
      question: 'Can I book same-day or last-minute cleaning?',
      answer: 'We\'ll do our best to accommodate last-minute bookings, but we recommend booking at least 48 hours in advance to ensure availability.',
    },
    {
      question: 'What if I\'m not satisfied with the cleaning?',
      answer: 'Your satisfaction is our priority. If you\'re not happy with any aspect of our service, contact us within 24 hours and we\'ll come back to re-clean at no extra charge.',
    },
    {
      question: 'Do you work on weekends?',
      answer: 'Yes, we offer cleaning services Monday through Saturday. We\'re flexible with scheduling to accommodate your needs.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section data-testid="services-hero" className="py-16 md:py-24 bg-gradient-to-br from-secondary/30 to-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
            Our Services
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Professional cleaning solutions for every need. From regular maintenance to deep cleaning and specialized services, we've got you covered.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section data-testid="services-list" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl space-y-16">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div
                key={index}
                data-testid={`service-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}
              >
                <div className={isEven ? '' : 'lg:col-start-2'}>
                  <div className="mb-4">
                    <Icon className="w-12 h-12 text-primary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                    {service.title}
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground mb-6">
                    {service.description}
                  </p>

                  <Accordion type="single" collapsible className="mb-6">
                    <AccordionItem value="included">
                      <AccordionTrigger data-testid={`service-${index}-accordion`}>
                        What's Included
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2">
                          {service.included.map((item, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Button
                    asChild
                    data-testid={`service-${index}-quote-btn`}
                    className="rounded-full bg-primary hover:bg-primary/90 text-white"
                  >
                    <Link to="/#quote-form">Request Quote</Link>
                  </Button>
                </div>

                <div className={isEven ? 'lg:col-start-2' : 'lg:col-start-1 lg:row-start-1'}>
                  <div className="relative overflow-hidden rounded-2xl shadow-soft">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-16 md:py-24 bg-surface">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                data-testid={`faq-${index}`}
                className="bg-white rounded-lg px-6 border border-border"
              >
                <AccordionTrigger className="text-left font-semibold text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="services-cta" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            Ready to Experience the BrightEdge Difference?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8">
            Get a free, no-obligation quote today
          </p>
          <Button
            asChild
            data-testid="services-cta-btn"
            className="rounded-full bg-primary hover:bg-primary/90 text-white px-12 py-6 text-lg"
          >
            <Link to="/#quote-form">Get Your Free Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;